import { useMemo } from 'react'
import { useLocation } from 'react-router'
import type { SidebarEntry } from '@carouselgg/nexus/sidebar'
import type { LogoRailItem } from '@carouselgg/nexus/logo-rail'
import { BarChart3, Bell, Building2, Home, Newspaper, ShieldCheck, Trophy } from '@carouselgg/nexus/icon'
import { useAuthStore } from '@carouselgg/auth/store'
import { useMyOrgs, type MyOrg } from '@carouselgg/auth/use-my-orgs'
import { useMyTeams, type MyTeam } from '@carouselgg/auth/use-my-teams'
import { Mark } from './mainNav'
import { manageItems, railGroups } from './railItems'

/**
 * The main side nav's sections, shared by the app shell and an org's
 * management area (see mainNav.tsx for the header that sits above them).
 */

/**
 * Top to bottom, from "what's going on" to "what's mine": home and alerts,
 * then where to find things, then what you have. Starting anything new is the
 * Join or Start button above all of it.
 */
function places(username: string | undefined): SidebarEntry[] {
  return [
    {
      kind: 'section',
      id: 'home',
      items: [
        { kind: 'item', id: 'home', label: 'Home', icon: <Home size={18} />, to: '/dashboard', end: true },
        // Nothing to be notified of without an account.
        ...(username
          ? [{ kind: 'item' as const, id: 'notifications', label: 'Notifications', icon: <Bell size={18} />, to: '/notifications' }]
          : []),
      ],
    },
    {
      kind: 'section',
      id: 'find',
      label: 'Find',
      items: [
        { kind: 'item', id: 'compete', label: 'Competitions', icon: <Trophy size={18} />, to: '/compete' },
        { kind: 'item', id: 'orgs', label: 'Clubs & leagues', icon: <Building2 size={18} />, to: '/orgs' },
        { kind: 'item', id: 'news', label: 'News', icon: <Newspaper size={18} />, to: '/news' },
      ],
    },
    ...(username
      ? [{
          kind: 'section' as const,
          id: 'yours',
          label: 'Yours',
          items: [
            { kind: 'item' as const, id: 'stats', label: 'My stats', icon: <BarChart3 size={18} />, to: `/u/${username}/stats` },
            { kind: 'item' as const, id: 'trust', label: 'Trust & accounts', icon: <ShieldCheck size={18} />, to: '/trust' },
          ],
        }]
      : []),
  ]
}

export interface MainNav {
  sections: SidebarEntry[]
  teams: MyTeam[]
  orgs: MyOrg[]
  /** The logo rail's three groups: your teams, orgs you run, orgs you're in. */
  groups: [LogoRailItem[], LogoRailItem[], LogoRailItem[]]
  isAuthenticated: boolean
}

/**
 * The places, then "You run" — the orgs you run or coach in and the teams you
 * captain, each one click from its management. Collapsed, your teams and orgs
 * fold in below so there is only one column of icons.
 */
export function useMainNav({ collapsed }: { collapsed?: boolean } = {}): MainNav {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const teams = useMyTeams(isAuthenticated)
  const orgs = useMyOrgs(isAuthenticated)
  const { pathname } = useLocation()
  const groups = useMemo(() => railGroups(teams, orgs, pathname), [teams, orgs, pathname])
  const manage = useMemo(() => manageItems(teams, orgs, pathname), [teams, orgs, pathname])
  const username = isAuthenticated ? user?.username : undefined

  const sections = useMemo<SidebarEntry[]>(() => {
    const base = places(username)
    const withRun: SidebarEntry[] = manage.length === 0 ? base : [
      ...base,
      {
        kind: 'section',
        id: 'manage',
        label: 'You run',
        items: manage.map((m) => ({ kind: 'item', id: m.id, label: m.label, icon: <Mark item={m} />, to: m.to })),
      },
    ]
    if (!collapsed) return withRun
    // One section per rail group, so the collapsed rail keeps the same breaks:
    // your teams, the orgs you run, the orgs you're part of.
    const labels = ['Your teams', 'Orgs you run', 'Orgs you\'re in']
    const yours: SidebarEntry[] = groups.flatMap((tiles, i) => tiles.length === 0 ? [] : [{
      kind: 'section' as const,
      id: `yours-${i}`,
      label: labels[i],
      items: tiles.map((t) => ({ kind: 'item' as const, id: t.id, label: t.label, icon: <Mark item={t} />, to: t.to })),
    }])
    return [...withRun, ...yours]
  }, [collapsed, groups, manage, username])

  return { sections, teams, orgs, groups, isAuthenticated }
}

